import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import GlobalHeader from '../components/GlobalHeader';
import Footer from '../components/Footer';
import { EnvelopeSimple, Phone, MapPin, PaperPlaneRight } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/api/contact`, formData);
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  React.useEffect(() => {
    document.title = 'Contact Us — Kavyalekh';
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />

      {/* Header */}
      <div className="bg-card border-b border-border py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <h1
            className="text-4xl md:text-5xl font-bold mb-3"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Contact Us
          </h1>
          <p className="text-lg text-muted-foreground">
            We'd love to hear from you. Get in touch with the Kavyalekh team.
          </p>
        </div>
      </div>

      {/* Contact Content */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <h2
              className="text-2xl font-bold mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Send us a Message
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold mb-2">Your Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Subject *</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="What is this about?"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
                  placeholder="Tell us more about your inquiry..."
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  'Sending...'
                ) : (
                  <>
                    <PaperPlaneRight size={20} weight="fill" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div>
            <h2
              className="text-2xl font-bold mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Get in Touch
            </h2>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <EnvelopeSimple size={24} weight="duotone" className="text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Email</h3>
                  <p className="text-sm text-muted-foreground mb-1">General Inquiries:</p>
                  <a href="mailto:support@kavyalekh.com" className="text-primary hover:underline">
                    support@kavyalekh.com
                  </a>
                  <p className="text-sm text-muted-foreground mb-1 mt-2">Business:</p>
                  <a href="mailto:business@kavyalekh.com" className="text-primary hover:underline">
                    business@kavyalekh.com
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Phone size={24} weight="duotone" className="text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Phone</h3>
                  <p className="text-muted-foreground">+91-XXXX-XXXXXX</p>
                  <p className="text-sm text-muted-foreground">Mon-Fri, 10 AM - 6 PM (IST)</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <MapPin size={24} weight="duotone" className="text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Office</h3>
                  <p className="text-sm text-muted-foreground">
                    Kavyalekh Literary Foundation<br />
                    [Office Address Line 1]<br />
                    [City, State - Pincode]<br />
                    India
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 bg-card border border-border rounded-lg">
              <h3 className="font-semibold mb-3">Business Hours</h3>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>Monday - Friday: 10:00 AM - 6:00 PM (IST)</li>
                <li>Saturday: 10:00 AM - 2:00 PM (IST)</li>
                <li>Sunday: Closed</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
